import { Action, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import * as _ from 'lodash';
import { AppStateModel } from './app.model';
import { AppService } from './app.service';
import { CreateProductScanData, GetProduct, QrProductFeedback, SetAuthenticated, SetTagNotFound, SetQRCode, SetTenantId, GetDownloadFilePath, SetPublicScanResults } from './app.actions';
import { Router } from '@angular/router';
import { NotificationService } from '../services/notification.service';

@State<AppStateModel>({
  name: 'App',
  defaults: {
    product: null,
    isProcessing: false,
    isAuthenticated: false,
    isTagNotFound: false,
    qrCode: null,
    tenantId: null,
    downloadFilePath: null,
    publicScanResults: null,
  },
})
@Injectable({
  providedIn: 'root',
})
export class AppState {
  constructor(
    private appService: AppService,
    private router: Router,
    private notificationService: NotificationService
  ) { }

  @Action(GetProduct)
  getProduct(
    { patchState }: StateContext<AppStateModel>,
    action: GetProduct
  ) {
    patchState({ isProcessing: true });
    return this.appService.getProduct(action.qrCode, action.tenantId, action.gtin).pipe(
      tap(async res => {
        patchState({
          product: res.payload,
          isProcessing: false,
        });
      }),
      catchError(async error => {
        patchState({ isProcessing: false });
        if (error.status === 404) {
          this.router.navigateByUrl('/not-found');
        } else {
          this.router.navigateByUrl('/qrcode-error');
        }
      })
    );
  }

  @Action(QrProductFeedback)
  qrProductFeedback(
    { patchState }: StateContext<AppStateModel>,
    action: QrProductFeedback
  ) {
    patchState({ isProcessing: true });
    return this.appService.qrProductFeedback(action.payload, action.tenantId).pipe(
      tap(async res => {
        patchState({
          isProcessing: false,
        });
      }),
      catchError(async error => {
        patchState({ isProcessing: false });
      })
    );
  }

  @Action(CreateProductScanData)
  createProductScanData(
    { patchState }: StateContext<AppStateModel>,
    action: CreateProductScanData
  ) {
    return this.appService.createProductScanData(action.payload, action.tenantId).pipe(
      tap(async res => {
      }),
      catchError(async error => {
        patchState({ isProcessing: false });
      })
    );
  }

  @Action(SetAuthenticated)
  setAuthenticated(
    { patchState }: StateContext<AppStateModel>,
    action: SetAuthenticated
  ) {
    patchState({ isAuthenticated: action.isAuthenticated });
  }

  @Action(SetTagNotFound)
  setTagNotFound(
    { patchState }: StateContext<AppStateModel>,
    action: SetTagNotFound
  ) {
    patchState({ isTagNotFound: action.isTagNotFound });
  }

  @Action(SetQRCode)
  setQRCode(
    { patchState }: StateContext<AppStateModel>,
    action: SetQRCode
  ) {
    patchState({ qrCode: action.qrCode });
  }

  @Action(SetTenantId)
  setTenantId(
    { patchState }: StateContext<AppStateModel>,
    action: SetTenantId
  ) {
    patchState({ tenantId: action.tenantId });
  }

  @Action(SetPublicScanResults)
  setPublicScanResults(
    { patchState }: StateContext<AppStateModel>,
    action: SetPublicScanResults
  ) {
    patchState({ publicScanResults: action.publicScanResults });
  }

  @Action(GetDownloadFilePath)
  getDownloadFilePath(
    { patchState }: StateContext<AppStateModel>,
    action: GetDownloadFilePath
  ) {
    patchState({ downloadFilePath: null });
    return this.appService.getDownloadFilePath(action.key, action.bucket).pipe(
      tap(response => {
        patchState({ downloadFilePath: response.payload.downloadUrl });
      }),
      catchError(async error => {
        patchState({ downloadFilePath: null });
        return throwError(() => new Error('Error fetching download file path: ' + error.message));
      })
    );
  }
}
